import * as React from 'react';
import { useEventCallback, useForkRef } from '../../../../hooks';
import { useHoneycombTheme } from '../../../../theming';
import { FixMeLater, ownerDocument } from '../../../../utils';
import { TypographyBody } from '../../../Typography';
import { Direction } from '../../useDateField/useDateField.types';
import { DigitalClockOptionProps } from '../DigitalClockOption/DigitalClockOption';

const nextItem = (listNode: HTMLElement, item: HTMLElement | null): HTMLElement => {
    if (item?.nextElementSibling) {
        return item.nextElementSibling as HTMLElement;
    }
    return listNode.firstChild as HTMLElement;
};

const previousItem = (listNode: HTMLElement, item: HTMLElement | null): HTMLElement => {
    if (item?.previousElementSibling) {
        return item.previousElementSibling as HTMLElement;
    }
    return listNode.lastChild as HTMLElement;
};

const moveFocus = (
    listNode: HTMLElement,
    currentFocus: HTMLElement | null,
    traversalFunction: (list: HTMLElement, item: HTMLElement | null) => HTMLElement
) => {
    let wrappedOnce = false;
    let nextFocus = traversalFunction(listNode, currentFocus);

    while (nextFocus) {
        // Prevent infinite loop.
        if (nextFocus === listNode.firstChild) {
            if (wrappedOnce) {
                return;
            }
            wrappedOnce = true;
        }

        const button = nextFocus.firstChild as HTMLElement;

        if (!button.hasAttribute('tabindex')) {
            // Move to the next element.
            nextFocus = traversalFunction(listNode, nextFocus);
        } else {
            button.focus();
            return;
        }
    }
};

export interface DigitalClockSectionProps {
    /**
     * The child `DigitalClockOption` components.
     */
    children?: Array<React.ReactElement<DigitalClockOptionProps>>;

    /**
     * An aria-label for the section.
     */
    'aria-label': string;

    /**
     * If `true`, the section is active.
     * @default false
     */
    active?: boolean;

    /**
     * If `true`, the active button will recieve focus.
     * @default false
     */
    autoFocus?: boolean;

    /**
     * A callback fired when the active section should be changed.
     * @default false
     */
    onChangeSection?: (dir: Direction) => void;

    /**
     * The current value for this section
     */
    activeValue: string | number | null;

    /**
     * If provided, the value will be displayed as a heading for the section.
     */
    heading?: string;
}

export const DigitalClockSection: React.ForwardRefExoticComponent<
    DigitalClockSectionProps & React.RefAttributes<HTMLElement>
> = React.forwardRef(function DigitalClock(
    props: DigitalClockSectionProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const {
        children,
        'aria-label': label,
        active = false,
        autoFocus = false,
        onChangeSection,
        activeValue,
        heading,
    } = props;

    const containerRef = React.useRef<HTMLDivElement>(null);
    const handleRef = useForkRef(ref, containerRef);

    const previousActiveButtonRef = React.useRef<HTMLElement | null>(null);

    const { theme } = useHoneycombTheme();
    const componentTheme = theme.components.themes.DigitalClock;

    const withHeading = Boolean(heading);

    const alreadyRendered = Boolean(containerRef.current);

    const handleKeyDown = useEventCallback((event: React.KeyboardEvent) => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const list = container.firstChild as HTMLElement;
        if (!list) {
            return;
        }

        const focusedButton = ownerDocument(container).activeElement as HTMLElement;
        const listItem = focusedButton.parentElement;

        // eslint-disable-next-line default-case
        switch (true) {
            case event.key === 'ArrowUp': {
                event.preventDefault();
                moveFocus(list, listItem, previousItem);
                break;
            }

            case event.key === 'ArrowDown': {
                event.preventDefault();
                moveFocus(list, listItem, nextItem);
                break;
            }

            case event.key === 'Home': {
                event.preventDefault();
                moveFocus(list, null, nextItem);
                break;
            }

            case event.key === 'End': {
                event.preventDefault();
                moveFocus(list, null, previousItem);
                break;
            }

            case event.key === 'ArrowLeft': {
                event.preventDefault();
                onChangeSection?.('prev');
                break;
            }

            case event.key === 'ArrowRight': {
                event.preventDefault();
                onChangeSection?.('next');
                break;
            }
        }
    });

    // Use effect
    React.useEffect(() => {
        const list = containerRef.current;
        if (!list) {
            return;
        }

        // Find the active button element
        const activeButton = list.querySelector<HTMLElement>(
            '[role="option"][tabindex="0"], [role="option"][aria-selected="true"]'
        );

        if (active && autoFocus && activeButton) {
            activeButton.focus();
        }

        if (!activeButton || previousActiveButtonRef.current === activeButton) {
            // There is no active button or the active button has not changed
            return;
        }

        previousActiveButtonRef.current = activeButton;

        // Scroll the active button to the top of the list
        const { offsetTop } = activeButton;
        list.scrollTop = offsetTop - (withHeading ? 0 : componentTheme.section.paddingY);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, autoFocus, activeValue]);

    return (
        <div
            ref={handleRef}
            data-digital-clock-section={true}
            data-mounted={alreadyRendered || undefined}
            data-subtle-scrollbars={true}
        >
            {withHeading && (
                <div data-digital-clock-section-heading={true}>
                    <TypographyBody component="div" size="xs" color="text.light" align="center">
                        {heading}
                    </TypographyBody>
                </div>
            )}

            <ul role="listbox" tabIndex={-1} aria-label={label} onKeyDown={handleKeyDown}>
                {children}
            </ul>
        </div>
    );
});
